// processDragResolve — the PURE drop-target resolver for structured step drag
// (plan §2/§5 P2). Given the ordered sibling rows (each a scene-geometry band,
// NOT a DOM measurement) and the pointer Y, decide which anchor the dragged step
// lands before/after. Kept pure + surface-agnostic so it is unit-tested with
// synthetic geometry and reused by every drag surface; the view supplies the
// bands and calls `stepEdit.moveStep(dragged, anchor, position)` on drop.

/** One draggable sibling row as a vertical band in canvas space. */
export interface DragRow {
  readonly nodeId: string;
  /** Band top / height in the same space the pointer Y is projected into. */
  readonly top: number;
  readonly height: number;
}

/** Where a drop lands: before/after a sibling anchor (the moveStep contract). */
export interface DropTarget {
  readonly anchorNodeId: string;
  readonly position: "before" | "after";
}

/**
 * Resolve a vertical drag drop among ordered sibling rows: the first row whose
 * MID-LINE sits below the pointer means "insert before that row"; past them all
 * means "after the last". The dragged row itself is excluded (it is the thing
 * moving). Returns null when there is no OTHER row to anchor to, or when the
 * resolved target is a no-op (dropping back into the dragged row's own slot) —
 * the caller skips the mutation so an idle drag never churns the text.
 */
export function resolveVerticalDrop(
  rows: readonly DragRow[],
  pointerY: number,
  draggedNodeId: string,
): DropTarget | null {
  const ordered = [...rows].sort((a, b) => a.top - b.top);
  const draggedIdx = ordered.findIndex((r) => r.nodeId === draggedNodeId);
  const others = ordered.filter((r) => r.nodeId !== draggedNodeId);
  if (others.length === 0) return null;

  // The insertion index among `others` (0..others.length): first row whose
  // midpoint is below the pointer.
  let insertAt = others.length;
  for (let i = 0; i < others.length; i++) {
    const r = others[i]!;
    if (pointerY < r.top + r.height / 2) {
      insertAt = i;
      break;
    }
  }

  // No-op guard: dropping into the dragged row's current slot. The rows BEFORE
  // the dragged one (all non-dragged) number exactly `draggedIdx`, so in the
  // `others`-indexed space its original slot is `insertAt === draggedIdx`.
  if (draggedIdx !== -1 && insertAt === draggedIdx) {
    return null;
  }

  if (insertAt >= others.length) {
    return { anchorNodeId: others[others.length - 1]!.nodeId, position: "after" };
  }
  return { anchorNodeId: others[insertAt]!.nodeId, position: "before" };
}

/** A block's body drop-zone rect in the same space as the pointer projection. */
export interface BlockZone {
  readonly blockNodeId: string;
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
}

/**
 * Resolve a drop INTO a block's body (the reparent primitive → moveStepInto):
 * the INNERMOST zone whose rect contains the pointer, excluding the dragged
 * node's own block (can't drop a block into itself). Innermost (smallest area)
 * wins so a nested block takes precedence over its enclosing ancestor. Returns
 * null when the pointer is over no block body — the caller then falls back to
 * sibling reorder.
 */
export function resolveBlockDrop(
  zones: readonly BlockZone[],
  pointerX: number,
  pointerY: number,
  draggedNodeId: string,
): string | null {
  let best: BlockZone | null = null;
  let bestArea = Infinity;
  for (const z of zones) {
    if (z.blockNodeId === draggedNodeId) continue;
    if (pointerX < z.left || pointerX > z.right || pointerY < z.top || pointerY > z.bottom) continue;
    const area = (z.bottom - z.top) * (z.right - z.left);
    if (area < bestArea) {
      best = z;
      bestArea = area;
    }
  }
  return best?.blockNodeId ?? null;
}
